/**
 * React Query hooks for Calibration APIs
 * 
 * Usage:
 * const { data: sessions, isLoading } = useCalibrationSessions({ year: 2025 })
 * const { mutate: createSession } = useCreateCalibrationSession()
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { calibrationService } from '@/services/api/calibrationService'
import type {
  CalibrationSession,
  CalibrationSessionCreate,
  CalibrationSessionUpdate,
  CalibrationSessionStatus,
  CalibrationType,
  PreCalibrationSubmission,
  PreCalibrationSubmissionCreate,
  PreCalibrationStatus,
  PerformanceRating,
  RatingAdjustment,
  RatingAdjustmentCreate,
  CalibrationReport,
  CalibrationAnalytics,
  MemberExclusionCreate,
} from '@/services/api/calibrationService'

// ============================================================================
// QUERY KEYS
// ============================================================================

export const calibrationKeys = {
  all: ['calibration'] as const,
  sessions: () => [...calibrationKeys.all, 'sessions'] as const,
  sessionsList: (filters: { year?: number; status?: CalibrationSessionStatus; org_unit_id?: string }) =>
    [...calibrationKeys.sessions(), 'list', filters] as const,
  session: (id: string) => [...calibrationKeys.sessions(), id] as const,
  availableManagers: (facilitatorId: string, type: string, year: number) =>
    [...calibrationKeys.sessions(), 'available-managers', facilitatorId, type, year] as const,
  chairs: (type: string, year: number) => [...calibrationKeys.sessions(), 'chairs', type, year] as const,
  submissions: (sessionId: string) => [...calibrationKeys.all, 'submissions', sessionId] as const,
  submissionsList: (sessionId: string, filters?: { manager_id?: string; status?: PreCalibrationStatus }) =>
    [...calibrationKeys.submissions(sessionId), 'list', filters] as const,
  adjustments: () => [...calibrationKeys.all, 'adjustments'] as const,
  pendingConfirmations: (managerId: string) =>
    [...calibrationKeys.adjustments(), 'pending', managerId] as const,
  analytics: (sessionId: string) => [...calibrationKeys.all, 'analytics', sessionId] as const,
}

// ============================================================================
// SESSION HOOKS
// ============================================================================

export function useCalibrationSessions(filters?: {
  year?: number
  type?: CalibrationType
  status?: CalibrationSessionStatus
  org_unit_id?: string
  viewer_id?: string
  scope?: 'mine' | 'all'
  q?: string
  limit?: number
  offset?: number
}, options?: {
  /** Keep the list on screen while another search, filter or page loads. */
  keepPrevious?: boolean
}) {
  return useQuery({
    queryKey: calibrationKeys.sessionsList(filters || {}),
    queryFn: () => calibrationService.listSessions(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: options?.keepPrevious ? keepPreviousData : undefined,
  })
}

export function useCalibrationSession(sessionId: string) {
  return useQuery({
    queryKey: calibrationKeys.session(sessionId),
    queryFn: () => calibrationService.getSession(sessionId),
    enabled: !!sessionId,
  })
}

/**
 * Managers the one-up can calibrate when scheduling a session, each flagged if
 * already rostered into another live session of the same type+year. Drives the
 * schedule dialog's manager checklist. Disabled until a facilitator id is known.
 */
export function useAvailableManagers(
  facilitatorId: string | undefined,
  type: CalibrationType,
  year: number,
  enabled = true
) {
  return useQuery({
    queryKey: calibrationKeys.availableManagers(facilitatorId || '', type, year),
    queryFn: () => calibrationService.getAvailableManagers(facilitatorId!, type, year),
    enabled: enabled && !!facilitatorId,
    staleTime: 60 * 1000,
  })
}

/**
 * The One-Ups HR can schedule a session for (HR only): HR schedules on a One-Up's
 * behalf and the One-Up stays the chair.
 */
export function useCalibrationChairs(type: CalibrationType, year: number, enabled = true) {
  return useQuery({
    queryKey: calibrationKeys.chairs(type, year),
    queryFn: () => calibrationService.listChairs(type, year),
    enabled: enabled && !!year,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateCalibrationSession() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: CalibrationSessionCreate) => calibrationService.createSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.sessions() })
    },
  })
}

export function useUpdateCalibrationSession() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: string; data: CalibrationSessionUpdate }) =>
      calibrationService.updateSession(sessionId, data),
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.session(sessionId) })
      queryClient.invalidateQueries({ queryKey: calibrationKeys.sessions() })
    },
  })
}

export function useDeleteCalibrationSession() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ sessionId, force = false }: { sessionId: string; force?: boolean }) => 
      calibrationService.deleteSession(sessionId, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.sessions() })
    },
  })
}

export function useBeginPreCalibration() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (sessionId: string) => calibrationService.beginPreCalibration(sessionId),
    onSuccess: (_, sessionId) => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.session(sessionId) })
      queryClient.invalidateQueries({ queryKey: calibrationKeys.sessions() })
    },
  })
}

export function useStartCalibrationSession() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (sessionId: string) => calibrationService.startSession(sessionId),
    onSuccess: (_, sessionId) => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.session(sessionId) })
      queryClient.invalidateQueries({ queryKey: calibrationKeys.sessions() })
    },
  })
}

export function useCompleteCalibrationSession() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (sessionId: string) => calibrationService.completeSession(sessionId),
    onSuccess: (_, sessionId) => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.session(sessionId) })
      queryClient.invalidateQueries({ queryKey: calibrationKeys.sessions() })
    },
  })
}

export function useGenerateCalibrationReport() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (arg: string | { sessionId: string; confirmDrift?: boolean }) => {
      const sessionId = typeof arg === 'string' ? arg : arg.sessionId
      const confirmDrift = typeof arg === 'string' ? undefined : arg.confirmDrift
      return calibrationService.generateReport(sessionId, { confirmDrift })
    },
    onSuccess: (_, arg) => {
      const sessionId = typeof arg === 'string' ? arg : arg.sessionId
      queryClient.invalidateQueries({ queryKey: calibrationKeys.session(sessionId) })
      // Also refresh the LIST query that the workspace renders from — without this
      // the session stays on the old phase after report generation (no refresh).
      queryClient.invalidateQueries({ queryKey: calibrationKeys.sessions() })
    },
  })
}

export function useCalibrationAnalytics(sessionId: string) {
  return useQuery({
    queryKey: calibrationKeys.analytics(sessionId),
    queryFn: () => calibrationService.getAnalytics(sessionId),
    enabled: !!sessionId,
  })
}

// ============================================================================
// SUBMISSION HOOKS
// ============================================================================

export function usePreCalibrationSubmissions(
  sessionId: string,
  filters?: { manager_id?: string; status?: PreCalibrationStatus }
) {
  return useQuery({
    queryKey: calibrationKeys.submissionsList(sessionId, filters),
    queryFn: () => calibrationService.listSubmissions(sessionId, filters),
    enabled: !!sessionId,
  })
}

export function useCreatePreCalibrationSubmission() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: string; data: PreCalibrationSubmissionCreate }) =>
      calibrationService.createSubmission(sessionId, data),
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.submissions(sessionId) })
    },
  })
}

export function useDeletePreCalibrationSubmission() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (submissionId: string) => calibrationService.deleteSubmission(submissionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all })
    },
  })
}

/** Bulk pre-calibration submit — one POST for the whole selection. */
export function useCreateSubmissionsBatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sessionId, submissions }: { sessionId: string; submissions: PreCalibrationSubmissionCreate[] }) =>
      calibrationService.createSubmissionsBatch(sessionId, submissions),
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.submissions(sessionId) })
      queryClient.invalidateQueries({ queryKey: calibrationKeys.sessions() })
    },
  })
}

/** PL declares their submissions complete (with any exclusions, atomically). */
export function useDeclareSubmissionsComplete() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sessionId, managerId, exclusions }: {
      sessionId: string; managerId: string; exclusions?: MemberExclusionCreate[]
    }) => calibrationService.declareSubmissionsComplete(sessionId, managerId, exclusions ?? []),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all })
    },
  })
}

export function useWithdrawDeclaration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sessionId, managerId }: { sessionId: string; managerId: string }) =>
      calibrationService.withdrawDeclaration(sessionId, managerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all })
    },
  })
}

/** Facilitator waives an unresponsive PL (or removes the waiver). */
export function useWaiveSubmitter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sessionId, managerId, waivedBy, reason }: {
      sessionId: string; managerId: string; waivedBy: string; reason: string
    }) => calibrationService.waiveSubmitter(sessionId, managerId, waivedBy, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all })
    },
  })
}

export function useSubmitPreCalibration() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (submissionId: string) => calibrationService.submitPreCalibration(submissionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all })
    },
  })
}

export function useFlagSubmission() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({
      submissionId,
      flagReason,
      flaggedBy,
    }: {
      submissionId: string
      flagReason: string
      flaggedBy: string
    }) => calibrationService.flagSubmission(submissionId, flagReason, flaggedBy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all })
    },
  })
}

export function useCalibrateSubmission() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({
      submissionId,
      calibratedRating,
      calibratedBy,
      calibrationNotes,
      kpaMotivations,
    }: {
      submissionId: string
      calibratedRating: PerformanceRating
      calibratedBy: string
      calibrationNotes?: string
      kpaMotivations?: { kpa_name: string; kpa_rating: string | null; motivation: string }[]
    }) => calibrationService.calibrateSubmission(submissionId, calibratedRating, calibratedBy, calibrationNotes, kpaMotivations),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.all })
    },
  })
}

// ============================================================================
// RATING ADJUSTMENT HOOKS
// ============================================================================

export function useCreateRatingAdjustment() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: string; data: RatingAdjustmentCreate }) =>
      calibrationService.createAdjustment(sessionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.adjustments() })
      queryClient.invalidateQueries({ queryKey: calibrationKeys.sessions() })
    },
  })
}

export function usePendingConfirmations(managerId: string) {
  return useQuery({
    queryKey: calibrationKeys.pendingConfirmations(managerId),
    queryFn: () => calibrationService.getPendingConfirmations(managerId),
    enabled: !!managerId,
  })
}

export function useScheduleDiscussion() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ adjustmentId, scheduledDate }: { adjustmentId: string; scheduledDate: string }) =>
      calibrationService.scheduleDiscussion(adjustmentId, scheduledDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.adjustments() })
    },
  })
}

export function useConfirmDiscussion() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ adjustmentId, notes }: { adjustmentId: string; notes?: string }) =>
      calibrationService.confirmDiscussion(adjustmentId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: calibrationKeys.adjustments() })
    },
  })
}
